import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2, CreditCard, Loader2, PackagePlus, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { sdk } from '@/lib/sdk'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { useSubscription } from '@/lib/SubscriptionContext'
import { useFormatters } from '@/lib/hooks/useFormatters'
import { useMoney } from '@/lib/hooks/useMoney'
import { parseAllowedPaymentMethods } from '@/lib/checkout/paymentMethods'
import {
  CustomerData,
  CustomerField,
  emptyCustomerData,
  accountDocument,
  nationalBrazilianPhone,
  toCustomerPayload,
  validateCustomerData
} from '@/lib/checkout/customer'
import { apiErrorMessage, CheckoutOutcome, interpretCheckoutResponse } from '@/lib/checkout/outcome'
import { PaymentMethodSelector } from '@/components/features/checkout/PaymentMethodSelector'
import { CustomerDataFields } from '@/components/features/checkout/CustomerDataFields'
import { PurchaseOutcomeView } from '@/components/features/checkout/PurchaseOutcomeView'
import { gaItem, rememberOrder, trackAddPaymentInfo, trackBeginCheckout, trackPurchase, type GaCart } from '@/lib/analytics/ecommerce'
import { getGaIds } from '@/lib/hooks/useGoogleAnalytics'
import { AddOnPrice } from '@/components/features/checkout/AddOnPrice'
import { yearlyInstallments } from '@/lib/checkout/annual'
import type { PaymentMethod, PlanAddOn, PlanAddOnAmount, PublicProduct, PublicSubscriptionGroup } from '@/types/checkout'
import type { AddOnQuote } from '@/types/subscription'
import { useMemberEntry } from '@/lib/hooks/useMemberEntry'
import { SuspendedNotice } from '@/components/features/memberEntry/SuspendedNotice'

interface AddAddOnDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** F-29 phase 3: opened from a store add-on card, this one is quoted right away. */
  preselectProductId?: string | null
}

interface Offer {
  addOn: PlanAddOn
  price: PlanAddOnAmount
}

type Done = { kind: 'added'; charged: boolean; nextChargeDate: string | null } | { kind: 'processing' }

/**
 * "Add an extension" on /billing (F-37, phase 3).
 *
 * Lists the plan's add-ons the member does not have yet, asks the API what
 * adding one costs (`/add-ons/quote`: the prorated amount until the plan's
 * next charge, or nothing under the floor), and adds it. The saved default
 * card pays by default; PIX and boleto work too and answer with the code to
 * pay. From the next charge on, the add-on is billed with the plan.
 */
export function AddAddOnDialog({ open, onOpenChange, preselectProductId = null }: AddAddOnDialogProps) {
  const { t } = useTranslation(['billing', 'checkout', 'common'])
  const { user } = useAuth()
  const { community } = useCommunity()
  const { subscription, paymentMethods, fetchSubscription, companyBilling } = useSubscription()
  const { formatDate } = useFormatters()
  const formatMoney = useMoney()
  /* F-86: a suspended community sells no add-on. */
  const { suspended: salesClosed } = useMemberEntry()

  const [offers, setOffers] = useState<Offer[]>([])
  const [planOption, setPlanOption] = useState<PublicProduct | null>(null)
  const [loadingOffers, setLoadingOffers] = useState(false)
  const [offersFailed, setOffersFailed] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [quote, setQuote] = useState<AddOnQuote | null>(null)
  const [quoting, setQuoting] = useState(false)
  const [quoteError, setQuoteError] = useState<string | null>(null)
  const [method, setMethod] = useState<PaymentMethod | null>(null)
  const [installments, setInstallments] = useState(1)
  const [customer, setCustomer] = useState<CustomerData>(emptyCustomerData)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<CustomerField, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<CheckoutOutcome | null>(null)
  const [done, setDone] = useState<Done | null>(null)

  const currency = subscription?.currency || community?.currency || 'BRL'
  const savedCard = paymentMethods.find((card) => card.isDefault) || paymentMethods[0] || null
  const owned = new Set((subscription?.addOns || []).filter((a) => a.status !== 'CANCELED' && a.status !== 'EXPIRED').map((a) => a.productId))

  /* The plan's add-ons come from the public catalog: the plan that holds this
     subscription's option, priced at that option's rhythm. */
  const loadOffers = useCallback(async (): Promise<Offer[]> => {
    if (!community?.id || !subscription) return []
    setLoadingOffers(true)
    setOffersFailed(false)
    try {
      const groups: PublicSubscriptionGroup[] = await sdk.checkout.getSubscriptionGroups()
      const list = Array.isArray(groups) ? groups : []
      const group = list.find((g) => g.products.some((p) => p.id === subscription.productId)) || null
      const option = group?.products.find((p) => p.id === subscription.productId) || null
      setPlanOption(option)
      const loaded = (group?.addOns || [])
        .filter((addOn) => addOn.sellsWithPlan)
        .map((addOn) => ({ addOn, price: option?.addOnAmounts?.find((a) => a.productId === addOn.productId) }))
        .filter((offer): offer is Offer => Boolean(offer.price))
      setOffers(loaded)
      return loaded
    } catch {
      setOffersFailed(true)
      return []
    } finally {
      setLoadingOffers(false)
    }
  }, [community?.id, subscription])

  useEffect(() => {
    if (!open) return
    setSelectedId(null)
    setQuote(null)
    setQuoteError(null)
    setSubmitError(null)
    setOutcome(null)
    setDone(null)
    setFieldErrors({})
    /* F-93 1B: a company's subscription is paid with the company's data (CNPJ), not the person's. */
    if (companyBilling) setCustomer((prev) => ({ ...prev, name: companyBilling.name || prev.name, email: companyBilling.email || prev.email, document: companyBilling.document || prev.document }))
    else setCustomer((prev) => ({ ...prev, name: prev.name || user?.name || '', email: prev.email || user?.email || '' }))
    // The phone is not in the session; prefilled from the account, as the checkout does.
    sdk.auth
      .me()
      .then((me) => {
        const phone = nationalBrazilianPhone(typeof me?.phone === 'string' ? me.phone : null)
        if (phone) setCustomer((prev) => ({ ...prev, phone: prev.phone || phone }))
        const document = companyBilling ? '' : accountDocument(me)
        if (document) setCustomer((prev) => ({ ...prev, document: prev.document || document }))
      })
      .catch(() => {})
    /* From a store card, the chosen add-on is quoted right away, so the
       dialog opens on its price instead of the pick list. */
    void loadOffers().then((loaded) => {
      if (preselectProductId && loaded.some((offer) => offer.addOn.productId === preselectProductId)) {
        void choose(preselectProductId)
      }
    })
    // Opening starts over; the offers depend on the subscription, read inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const allowed = parseAllowedPaymentMethods(planOption?.allowedPaymentMethods)
  /* The card is the saved one: a new card is added under "Credit card" on
     this page. Without one, PIX and boleto remain. */
  const methods = allowed.filter((m) => m !== 'CREDIT_CARD' || savedCard)
  // The plan option's card installments, as the plan shows them (F-36).
  const planInstallments = allowed.includes('CREDIT_CARD') ? yearlyInstallments(planOption) : 1

  const choose = async (productId: string) => {
    if (!subscription) return
    setSelectedId(productId)
    setQuote(null)
    setQuoteError(null)
    setSubmitError(null)
    setQuoting(true)
    try {
      const next: AddOnQuote = await sdk.subscriptions.quoteAddOn(subscription.id, productId)
      setQuote(next)
      const cart = addOnCart(productId, next)
      if (cart) trackBeginCheckout(cart)
      setMethod(methods.includes('CREDIT_CARD') ? 'CREDIT_CARD' : methods[0] ?? null)
      setInstallments(1)
    } catch (error) {
      setQuoteError(apiErrorMessage(error).message || t('addOn.quoteFailed'))
    } finally {
      setQuoting(false)
    }
  }

  /* The add-on as the community's Google Analytics sees it: what is charged now. */
  const addOnCart = (productId: string, priced: AddOnQuote): GaCart | null => {
    const offer = offers.find((candidate) => candidate.addOn.productId === productId)
    if (!offer) return null
    const value = parseFloat(priced.amountToCharge) || 0
    return {
      items: [gaItem({ id: productId, title: offer.addOn.title }, { category: 'add_on', price: value })],
      value,
      currency: priced.currency || currency
    }
  }

  const submit = async () => {
    if (!subscription || !selectedId || !quote || submitting) return
    setSubmitError(null)
    const charging = !quote.floorApplied
    if (charging && !method) {
      setSubmitError(t('addOn.noMethod'))
      return
    }
    if (charging && method !== 'CREDIT_CARD') {
      const errors = validateCustomerData(customer)
      setFieldErrors(errors)
      if (Object.values(errors).some(Boolean)) return
    }

    setSubmitting(true)
    const cart = addOnCart(selectedId, quote)
    try {
      if (charging && cart) trackAddPaymentInfo(cart, method)
      const gaIds = charging ? await getGaIds() : {}
      const response = await sdk.subscriptions.addAddOn(subscription.id, {
        productId: selectedId,
        ...gaIds,
        ...(charging && method
          ? {
              paymentMethod: method,
              ...(method === 'CREDIT_CARD'
                ? { paymentMethodId: savedCard?.id, installments }
                : { customerData: toCustomerPayload(customer) })
            }
          : {})
      })
      /* GA `purchase` once the charge is paid; a PIX or boleto is remembered
         for whichever screen sees it paid. Nothing charged now (under the
         floor) is not a purchase yet: it joins the plan's next charge. */
      if (response?.charged && response?.purchaseId && cart) {
        if (response.status === 'COMPLETED' && method === 'CREDIT_CARD') trackPurchase(response.purchaseId, cart, method)
        else rememberOrder(response.purchaseId, cart, method)
      }
      if (!response?.charged) {
        setDone({ kind: 'added', charged: false, nextChargeDate: response?.nextChargeDate ?? quote.nextChargeDate })
      } else if (method === 'PIX' || method === 'BOLETO') {
        setOutcome(interpretCheckoutResponse(response, method))
      } else if (response?.status === 'COMPLETED') {
        setDone({ kind: 'added', charged: true, nextChargeDate: response?.nextChargeDate ?? quote.nextChargeDate })
      } else {
        setDone({ kind: 'processing' })
      }
      void fetchSubscription()
    } catch (error) {
      setSubmitError(apiErrorMessage(error).message || t('addOn.genericError'))
    } finally {
      setSubmitting(false)
    }
  }

  const close = () => {
    if (!submitting) onOpenChange(false)
  }

  const day = (value: string | null | undefined) => (value ? formatDate(value, 'P') : '')
  const selectedOffer = offers.find((offer) => offer.addOn.productId === selectedId) || null
  const available = offers.filter((offer) => !owned.has(offer.addOn.productId))

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-1.5rem)] max-w-lg overflow-y-auto p-4 sm:p-6">
        <div className="min-w-0 space-y-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackagePlus className="h-5 w-5" />
              {t('addOn.title')}
            </DialogTitle>
            <DialogDescription>{t('addOn.description')}</DialogDescription>
          </DialogHeader>

          {outcome ? (
            <PurchaseOutcomeView
              outcome={outcome}
              onRetry={() => setOutcome(null)}
              onDone={() => onOpenChange(false)}
              doneLabel={t('close', { ns: 'common' })}
            />
          ) : done ? (
            <div className="flex items-start gap-3 rounded-lg bg-green-50 p-4 text-sm text-green-800">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <p>
                {done.kind === 'processing'
                  ? t('addOn.processing')
                  : done.charged
                    ? t('addOn.addedCharged', { date: day(done.nextChargeDate) })
                    : t('addOn.addedFree', { date: day(done.nextChargeDate) })}
              </p>
            </div>
          ) : loadingOffers ? (
            <div className="space-y-2" aria-busy="true">
              {[0, 1].map((row) => (
                <div key={row} className="h-16 animate-pulse rounded-lg bg-gray-100" />
              ))}
            </div>
          ) : offersFailed ? (
            <div className="flex flex-col items-center gap-2 py-4 text-center">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
              <p className="text-sm text-gray-700">{t('addOn.loadFailed')}</p>
              <Button variant="outline" size="sm" onClick={() => void loadOffers()}>
                <RotateCcw className="mr-2 h-4 w-4" />
                {t('purchase.tryAgain', { ns: 'checkout' })}
              </Button>
            </div>
          ) : available.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">
              {offers.length === 0 ? t('addOn.noneForPlan') : t('addOn.allAdded')}
            </p>
          ) : (
            <>
              <div role="radiogroup" aria-label={t('addOn.title')} className="space-y-2">
                {available.map(({ addOn, price }) => {
                  const active = selectedId === addOn.productId
                  return (
                    <button
                      key={addOn.productId}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => void choose(addOn.productId)}
                      disabled={submitting}
                      className={cn(
                        'w-full rounded-lg border-2 p-3 text-left transition-colors',
                        active ? 'border-primary bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="min-w-0 font-medium text-gray-900">{addOn.title}</span>
                        <AddOnPrice
                          className="flex-shrink-0"
                          amount={parseFloat(price.amount)}
                          interval={price.interval}
                          currency={addOn.currency}
                          installments={planInstallments}
                        />
                      </div>
                      {addOn.shortDescription && <p className="mt-0.5 text-xs text-gray-500">{addOn.shortDescription}</p>}
                    </button>
                  )
                })}
              </div>

              {quoting && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('purchase.calculating', { ns: 'checkout' })}
                </div>
              )}

              {quoteError && (
                <div role="alert" className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  {quoteError}
                </div>
              )}

              {quote && selectedOffer && (
                <div className="space-y-1 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                  {quote.floorApplied ? (
                    <p className="font-medium text-gray-900">
                      {t('addOn.quoteFree', {
                        date: day(quote.nextChargeDate),
                        amount: formatMoney(parseFloat(quote.periodAmount), currency)
                      })}
                    </p>
                  ) : (
                    <>
                      <p className="font-medium text-gray-900">
                        {t('addOn.quoteToday', {
                          amount: formatMoney(parseFloat(quote.amountToCharge), currency),
                          date: day(quote.nextChargeDate)
                        })}
                      </p>
                      <p className="text-gray-700">
                        {t('addOn.quoteThen', { amount: formatMoney(parseFloat(quote.periodAmount), currency) })}
                      </p>
                    </>
                  )}
                  {quote.nextChargeTotal && quote.nextChargeDate && (
                    <p className="text-xs text-gray-500">
                      {t('addOn.quoteNextCharge', {
                        amount: formatMoney(parseFloat(quote.nextChargeTotal), currency),
                        date: day(quote.nextChargeDate)
                      })}
                    </p>
                  )}
                </div>
              )}

              {quote && !quote.floorApplied && (
                <section className="space-y-3">
                  <h4 className="text-xs text-gray-500">{t('purchase.paymentMethod', { ns: 'checkout' })}</h4>
                  {methods.length > 0 ? (
                    <PaymentMethodSelector methods={methods} value={method} onChange={setMethod} disabled={submitting} />
                  ) : null}
                  {!savedCard && allowed.includes('CREDIT_CARD') && (
                    <p className="text-xs text-gray-500">{t('addOn.noSavedCard')}</p>
                  )}
                  {method === 'CREDIT_CARD' && savedCard && (
                    <>
                      <p className="flex items-center gap-2 text-sm text-gray-700">
                        <CreditCard className="h-4 w-4 text-gray-400" />
                        {t('addOn.savedCard', {
                          brand: (savedCard.cardBrand || '').replace(/^./, (c) => c.toUpperCase()),
                          last4: savedCard.cardLastDigits || ''
                        })}
                      </p>
                      {quote.maxInstallments > 1 && (
                        <div>
                          <label htmlFor="addon-installments" className="mb-1 block text-xs text-gray-500">
                            {t('purchase.fields.installments', { ns: 'checkout' })}
                          </label>
                          <select
                            id="addon-installments"
                            value={installments}
                            onChange={(e) => setInstallments(Number(e.target.value))}
                            disabled={submitting}
                            className="h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                          >
                            {Array.from({ length: quote.maxInstallments }, (_, i) => i + 1).map((count) => (
                              <option key={count} value={count}>
                                {t('purchase.installmentOptionAmount', {
                                  ns: 'checkout',
                                  count,
                                  amount: formatMoney(parseFloat(quote.amountToCharge) / count, currency)
                                })}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </>
                  )}
                  {(method === 'PIX' || method === 'BOLETO') && (
                    <CustomerDataFields
                      value={customer}
                      errors={fieldErrors}
                      onChange={(field, value) => {
                        setCustomer((prev) => ({ ...prev, [field]: value }))
                        setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
                      }}
                      disabled={submitting}
                    />
                  )}
                </section>
              )}

              {submitError && (
                <div role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  {submitError}
                </div>
              )}
            </>
          )}

          {!outcome && !done && <SuspendedNotice kind="sale" />}

          <DialogFooter className="gap-2">
            {outcome ? null : done ? (
              <Button className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>
                {t('close', { ns: 'common' })}
              </Button>
            ) : (
              <>
                <Button variant="outline" className="w-full sm:w-auto" onClick={close} disabled={submitting}>
                  {t('addOn.cancel')}
                </Button>
                <Button
                  variant="secondary"
                  className="w-full sm:w-auto"
                  onClick={() => void submit()}
                  disabled={!quote || submitting || quoting || salesClosed}
                >
                  {submitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  {quote?.floorApplied ? t('addOn.confirmFree') : t('addOn.confirm')}
                </Button>
              </>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
